import { useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RuleBuilder } from "@/components/RuleBuilder";
import { ArrowRightLeft, UserPlus, UserMinus, Loader2 } from "lucide-react";

// Converts an assigned group into a dynamic (rule-based) one, previewing joins/leaves first.
// apiBase: "/security-groups" | "/m365-groups"
export function ConvertToDynamicDialog({ group, apiBase, onClose, onDone }) {
  const { t } = useLang();
  const [rule, setRule] = useState("");
  const [state, setState] = useState({ clauses: [], join: "and" });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const onChange = (r, extra) => { setRule(r); if (extra) setState(extra); };

  const runPreview = async () => {
    if (!state.clauses.length) { toast.error(t("convert.needRule")); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/groups/${group.id}/convert-preview`, { kind: "entra", join: state.join, clauses: state.clauses });
      setPreview(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      await api.patch(`${apiBase}/${group.id}`, { membershipType: "dynamic", membershipRule: rule });
      toast.success(t("convert.done")); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setApplying(false); }
  };

  const List = ({ title, icon: Icon, people, tone, testid }) => (
    <div className="flex-1">
      <div className={`mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider ${tone}`}><Icon size={13} /> {title} ({people.length})</div>
      <div className="max-h-40 overflow-y-auto border border-zinc-200" data-testid={testid}>
        {people.length === 0 ? <p className="px-3 py-3 text-xs text-zinc-400">—</p> :
          people.map((p) => <div key={p.id} className="border-b border-zinc-100 px-3 py-1.5 text-xs last:border-0"><span className="font-medium text-zinc-800">{p.displayName}</span> <span className="text-zinc-400">{p.userPrincipalName || p.mail}</span></div>)}
      </div>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm"><ArrowRightLeft size={16} /> {t("convert.title")} — {group.displayName}</DialogTitle>
          <DialogDescription className="text-xs">{t("convert.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-violet-200 bg-violet-50/40 p-3">
            <RuleBuilder value="" onChange={onChange} testid="convert-rule" variant="entra" preview templates />
          </div>
          <Button type="button" variant="outline" onClick={runPreview} disabled={loading} data-testid="convert-preview-btn" className="rounded-sm">
            {loading && <Loader2 size={14} className="mr-2 animate-spin" />}{t("convert.previewBtn")}
          </Button>
          {preview && (
            <div className="flex gap-3" data-testid="convert-preview-result">
              <List title={t("convert.joins")} icon={UserPlus} people={preview.joins} tone="text-emerald-600" testid="convert-joins" />
              <List title={t("convert.leaves")} icon={UserMinus} people={preview.leaves} tone="text-red-600" testid="convert-leaves" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">{t("common.cancel")}</Button>
          <Button onClick={apply} disabled={applying || !preview} data-testid="convert-apply" className="rounded-sm bg-primary hover:bg-[#002480]">
            {applying && <Loader2 size={14} className="mr-2 animate-spin" />}{t("convert.apply")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
